import { useState, useCallback, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import {
  getLessonPlans,
  markLessonPlanComplete,
  flagLessonPlan,
  deleteLessonPlan,
} from "../../../services/LessonPlanService";
import { handleApiError } from "../../../utils/errorHandler";
import { WEEK_PER_PAGE } from "../LessonPlannerConstant";
import {
  getCurrentUserId,
  getSuccessMessage,
  getWeekRange,
  normalizeLessonList,
  toApiDate,
} from "../utils/lessonPlanUtils";

const EMPTY_SUMMARY = { total: 0, completed: 0, pending: 0, flagged: 0 };

export const useLessonPlans = () => {
  const [lessons, setLessons] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(false);
  // id of the lesson an action (complete / flag / delete) is running for
  const [actionId, setActionId] = useState(null);

  const weekRef = useRef(null);
  // Ignore responses from an older week if the user navigates quickly
  const requestRef = useRef(0);

  /* ================= FETCH ================= */
  const fetchLessons = useCallback(async (weekStart) => {
    weekRef.current = weekStart;
    const requestId = ++requestRef.current;
    const { start, end } = getWeekRange(weekStart);

    setIsLoading(true);
    try {
      const response = await getLessonPlans({
        // Always the logged-in teacher's own plans, even with lesson_plan.view_all
        teacher_id: getCurrentUserId(),
        lesson_date_from: toApiDate(start),
        lesson_date_to: toApiDate(end),
        sort_by: "lesson_date",
        sort_order: "asc",
        per_page: WEEK_PER_PAGE,
      });
      if (requestId !== requestRef.current) return;

      const { lessons, summary } = normalizeLessonList(response);
      setLessons(lessons);
      setSummary(summary);
    } catch (error) {
      if (requestId === requestRef.current) handleApiError(error);
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, []);

  /* ================= ACTIONS ================= */
  const runAction = useCallback(
    async (id, request, fallbackMessage) => {
      setActionId(id);
      try {
        const response = await request();
        enqueueSnackbar(getSuccessMessage(response, fallbackMessage), { variant: "success" });
        await fetchLessons(weekRef.current);
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      } finally {
        setActionId(null);
      }
    },
    [fetchLessons],
  );

  const markComplete = useCallback(
    (id, remarks) => runAction(id, () => markLessonPlanComplete(id, { remarks }), "Lesson marked as complete"),
    [runAction],
  );

  const flagLesson = useCallback(
    (id, { reason, remarks, revisedDate }) =>
      runAction(
        id,
        () =>
          flagLessonPlan(id, {
            reason,
            remarks: remarks || null,
            revised_delivery_date: revisedDate || null,
          }),
        "Lesson flagged",
      ),
    [runAction],
  );

  const removeLesson = useCallback(
    (id) => runAction(id, () => deleteLessonPlan(id), "Lesson plan deleted"),
    [runAction],
  );

  return {
    lessons,
    summary,
    isLoading,
    actionId,
    fetchLessons,
    markComplete,
    flagLesson,
    removeLesson,
  };
};
